import { ReactNode, useMemo, useState } from "react";
import { useViewport } from "src/contexts";
import { useModal } from "src/contexts/Modal.context";
import {
  ConfiguratorStepper,
  ImageDisplay,
  ReserveYourBuildFlyout,
  StepWrapper,
  SummaryCardList,
} from "src/pages/configurator/components";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { PDFPrintModal } from "src/pages/configuratorPrint/PDFPrintModal";
import { createConfigurationsDownloadUrl } from "src/RouteUrls";
import { useCurrentUser } from "src/utils/hooks/useCurrentUser";
import { legacyHasSelectedAduOption } from "src/utils/summary.utils";
import { ButtonProps, getSelectedStructuralOptionCodes } from "~components";
import { Css, Palette } from "~generated/css";
import { CheckoutConfigStatus } from "~generated/graphqlTypes";
import { dateFormatter, useTestIds } from "~utils";
import { getCCFGImages } from "../Configurator.utils";

// Hardcoding this for now to match the figma - unclear how this will be managed at this point
export const EXPIRATION_DAYS = 14;

type StepConfig = {
  title: ReactNode;
  description: string;
  tag?: {
    children: string;
    backgroundColor: Palette;
  };
  actionBts: ButtonProps[];
};

export function SummaryStep() {
  const { goToStep, checkoutConfig, goToPreviousStep, selectedOptionIds, hasAduSupport } = useConfiguratorContext();
  const { isMobile } = useViewport();
  const tid = useTestIds({}, `summary-step`);
  const { showModal, closeModal } = useModal();
  const { currentUser, isHomeowner } = useCurrentUser();
  const currentUserEmail = currentUser?.email;
  const currentUserName = currentUser?.name;

  const [showReserveFlyout, setShowReserveFlyout] = useState(false);

  const images = useMemo(() => getCCFGImages(checkoutConfig), [checkoutConfig]);

  // passed into floor plan component
  const selectedStructuralOptionCodes = useMemo(
    () => getSelectedStructuralOptionCodes(checkoutConfig?.optionGroups ?? [], selectedOptionIds ?? []),
    [checkoutConfig, selectedOptionIds],
  );

  const allowAduReport = hasAduSupport && legacyHasSelectedAduOption(checkoutConfig?.optionGroups ?? []);

  const downloadPdfButton: ButtonProps = {
    children: "Download PDF",
    variant: "secondary",
    disabled: !checkoutConfig,
    onClick: () =>
      checkoutConfig &&
      showModal(
        <PDFPrintModal
          onClose={closeModal}
          checkoutConfigId={checkoutConfig.id}
          allowAduReport={allowAduReport}
          onContinue={(reports, includePricing) =>
            window.open(createConfigurationsDownloadUrl(checkoutConfig.id, reports, includePricing), "_blank")
          }
        />,
      ),
  };

  const pageConfig: Record<CheckoutConfigStatus, StepConfig> = {
    [CheckoutConfigStatus.Draft]: {
      title: "Home Summary",
      description: `Reserve your home to lock in pricing for ${EXPIRATION_DAYS} days and view your build at any time.`,
      actionBts: [
        {
          children: "Back",
          variant: "link",
          color: "white",
          onClick: () => goToPreviousStep(),
        },
        downloadPdfButton,
        {
          children: isMobile ? "Save" : "Reserve",
          variant: "secondary",
          onClick: () => setShowReserveFlyout(true),
        },
      ],
    },
    [CheckoutConfigStatus.Reserved]: {
      title: (
        <div css={Css.df.fdc.gap1.$}>
          <div css={Css.brightGreen.$}>Your Home is Reserved!</div>
          <div css={Css.t12.$}>{dateFormatter(checkoutConfig?.reservedAt ?? new Date(), "MMMM d, yyyy")}</div>
        </div>
      ),
      description: `Your pricing is locked in for ${EXPIRATION_DAYS} days. Download your PDF below to attach to a contract.`,
      actionBts: [downloadPdfButton],
    },
    [CheckoutConfigStatus.Expired]: {
      title: "Configuration expired",
      description: `Configuration pricing expires after ${EXPIRATION_DAYS} days. Design another home for new pricing.`,
      tag: { children: "Expired", backgroundColor: Palette.Red100 },
      actionBts: [],
    },
  };

  const { title, description, tag, actionBts } = pageConfig[checkoutConfig?.status ?? CheckoutConfigStatus.Draft];

  return (
    <>
      <StepWrapper
        loading={!checkoutConfig}
        loadingView="accordion"
        title={title}
        description={description}
        tag={tag}
        left={
          <ImageDisplay
            images={images}
            selectedOptionCodes={selectedStructuralOptionCodes}
            isReversed={checkoutConfig?.isReversed}
            {...tid.imageDisplay}
          />
        }
        right={checkoutConfig ? <SummaryCardList checkoutConfig={checkoutConfig} goToStep={goToStep} /> : <></>}
      />
      {showReserveFlyout && checkoutConfig && (
        <ReserveYourBuildFlyout
          onClose={() => setShowReserveFlyout(false)}
          checkoutConfig={checkoutConfig}
          expirationInDays={EXPIRATION_DAYS}
          isSummaryStep
          currentUserEmail={currentUserEmail}
          currentUserName={currentUserName}
          isHomeowner={isHomeowner}
        />
      )}
      <ConfiguratorStepper actionBts={actionBts} />
    </>
  );
}
